import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LocationPicker from './LocationPicker';

const FilterContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SearchInputContainer = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm} 40px;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.body.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: ${props => props.theme.colors.textMuted};
`;

const FilterToggle = styled.button.withConfig({
  shouldForwardProp: (prop) => !['expanded'].includes(prop),
})`
  background: ${props => props.$expanded ? props.theme.colors.gradient : 'transparent'};
  border: 1px solid ${props => props.$expanded ? 'transparent' : props.theme.colors.border};
  color: ${props => props.$expanded ? 'white' : props.theme.colors.text.primary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.caption.fontSize};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$expanded ? props.theme.colors.gradientHover : props.theme.colors.surfaceVariant};
    border-color: ${props => props.$expanded ? 'transparent' : props.theme.colors.primary};
    color: ${props => props.$expanded ? 'white' : props.theme.colors.primary};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const FiltersGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const FilterLabel = styled.label`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  font-weight: 500;
`;

const FilterSelect = styled.select`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
  
  option {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const FilterInput = styled.input`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const ClearFiltersButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text.secondary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.caption.fontSize};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.accent};
    color: ${props => props.theme.colors.accent};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const FilterBar = ({ filters, onFilterChange, currentLocation, onLocationChange, venues = [] }) => {
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const handleFilterChange = (key, value) => {
    onFilterChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFilterChange({
      search: '',
      genre: '',
      dateFrom: null,
      dateTo: null,
      posterType: '',
      radius: 25
    });
  };

  const hasActiveFilters = filters.genre || filters.dateFrom || filters.dateTo || filters.posterType || (filters.radius && filters.radius !== 25);

  return (
    <FilterContainer>
      <SearchRow>
        <SearchInputContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search shows, bands, or venues..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </SearchInputContainer>
        
        <FilterToggle
          $expanded={filtersExpanded}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <FunnelIcon />
          Filters
        </FilterToggle>
        
        {hasActiveFilters && (
          <ClearFiltersButton onClick={clearFilters}>
            <XMarkIcon />
            Clear
          </ClearFiltersButton>
        )}
      </SearchRow>

      <AnimatePresence>
        {filtersExpanded && (
          <FiltersGrid
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FilterGroup>
              <FilterLabel>Location</FilterLabel>
              <LocationPicker 
                currentLocation={currentLocation}
                onLocationChange={onLocationChange}
              />
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Genre</FilterLabel>
              <FilterSelect
                value={filters.genre || ''}
                onChange={(e) => handleFilterChange('genre', e.target.value)}
              >
                <option value="">All genres</option>
                <option value="rock">Rock</option>
                <option value="indie">Indie</option>
                <option value="punk">Punk</option>
                <option value="metal">Metal</option>
                <option value="jazz">Jazz</option>
                <option value="blues">Blues</option>
                <option value="electronic">Electronic</option>
                <option value="hip_hop">Hip Hop</option>
                <option value="folk">Folk</option>
                <option value="country">Country</option>
                <option value="other">Other</option>
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Date From</FilterLabel>
              <FilterInput
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value || null)}
              />
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Date To</FilterLabel>
              <FilterInput
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e.target.value || null)}
              />
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Posted by</FilterLabel>
              <FilterSelect
                value={filters.posterType || ''}
                onChange={(e) => handleFilterChange('posterType', e.target.value)}
              >
                <option value="">All posters</option>
                <option value="fan">Fans</option>
                <option value="band">Bands</option>
                <option value="venue">Venues</option>
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Radius</FilterLabel>
              <FilterSelect
                value={filters.radius || 25}
                onChange={(e) => handleFilterChange('radius', parseInt(e.target.value))}
              >
                <option value={10}>10 miles</option>
                <option value={25}>25 miles</option>
                <option value={50}>50 miles</option>
                <option value={100}>100 miles</option>
              </FilterSelect>
            </FilterGroup>
          </FiltersGrid>
        )}
      </AnimatePresence>
    </FilterContainer>
  );
};

export default FilterBar; 